import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DiagramFrame, Reveal, SceneHead, routesFor, type Lang } from "../primitives";

/**
 * Diagramas propios: explican el concepto del servicio.
 * No representan datos reales y no usan ejes de ventas ni cifras.
 */

/** SEO técnico: arquitectura de páginas y acceso del rastreador. */
const DiagramArchitecture = ({ label }: { label: string }) => (
  <svg viewBox="0 0 220 120" className="h-full w-full" role="img" aria-label={label}>
    <g fill="none" stroke="var(--v3-line)" strokeWidth="1">
      <rect x="86" y="8" width="48" height="20" />
      <rect x="18" y="52" width="44" height="18" />
      <rect x="88" y="52" width="44" height="18" />
      <rect x="158" y="52" width="44" height="18" />
      <rect x="18" y="94" width="44" height="18" />
      <rect x="88" y="94" width="44" height="18" />
    </g>
    <g stroke="var(--v3-blue)" strokeWidth="1.3" fill="none">
      <path d="M110 28 V40 H40 V52" />
      <path d="M110 28 V52" />
      <path d="M110 28 V40 H180 V52" />
      <path d="M40 70 V94" />
      <path d="M110 70 V94" />
    </g>
    <circle cx="110" cy="18" r="3" fill="var(--v3-copper)" />
  </svg>
);

/** SEO local: cobertura de servicio conceptual, sin mapa ni oficina ficticia. */
const DiagramCoverage = ({ label }: { label: string }) => (
  <svg viewBox="0 0 220 120" className="h-full w-full" role="img" aria-label={label}>
    <circle cx="110" cy="62" r="46" fill="none" stroke="var(--v3-line)" />
    <circle cx="110" cy="62" r="28" fill="none" stroke="var(--v3-line)" />
    <circle cx="110" cy="62" r="5" fill="var(--v3-copper)" />
    <g stroke="var(--v3-blue)" strokeWidth="1.2" fill="none">
      <path d="M110 62 L74 42" />
      <path d="M110 62 L146 50" />
      <path d="M110 62 L132 92" />
      <path d="M110 62 L84 86" />
    </g>
    <g fill="var(--v3-blue)">
      <circle cx="74" cy="42" r="3" />
      <circle cx="146" cy="50" r="3" />
      <circle cx="132" cy="92" r="3" />
      <circle cx="84" cy="86" r="3" />
    </g>
  </svg>
);

/** Google Ads: búsqueda → página de destino → contacto medido. */
const DiagramFunnel = ({ label }: { label: string }) => (
  <svg viewBox="0 0 220 120" className="h-full w-full" role="img" aria-label={label}>
    <g fill="none" stroke="var(--v3-line)" strokeWidth="1">
      <rect x="18" y="46" width="44" height="28" />
      <rect x="84" y="46" width="52" height="28" />
      <rect x="158" y="46" width="44" height="28" />
    </g>
    <g stroke="var(--v3-blue)" strokeWidth="1.4" fill="none">
      <path d="M62 60 H84" />
      <path d="M136 60 H158" />
    </g>
    <g stroke="var(--v3-copper)" strokeWidth="1.2" fill="none" strokeDasharray="4 4">
      <path d="M184 76 V100 H36 V76" />
    </g>
    <circle cx="184" cy="60" r="3.5" fill="var(--v3-blue)" />
  </svg>
);

const t = {
  es: {
    aria: "Servicios prioritarios",
    label: "Servicios prioritarios",
    title: "Tres frentes para convertir búsquedas en oportunidades.",
    secondaryLabel: "Otros frentes, según el proyecto",
    services: [
      {
        n: "01",
        name: "SEO técnico",
        key: "serviceTechnical",
        problem:
          "Identifica las barreras que dificultan que los buscadores encuentren y comprendan tu sitio.",
        scope:
          "Alcance: rastreo, indexación, arquitectura y rendimiento; implementación según contratación.",
        cta: "Ver SEO técnico",
        caption:
          "Arquitectura de páginas y acceso del rastreador. Esquema conceptual, no datos de tu sitio.",
        diagramLabel:
          "Diagrama conceptual: arquitectura de páginas, rastreo e indexación",
      },
      {
        n: "02",
        name: "SEO local",
        key: "localSeoHub",
        problem:
          "Relaciona tu oferta con las búsquedas de personas que necesitan tus servicios en una zona concreta.",
        scope:
          "Alcance: sitio, información del negocio y perfil local elegible. Sin posiciones prometidas ni oficinas inventadas.",
        cta: "Ver SEO local",
        caption:
          "Cobertura de servicio como concepto: oferta, zona atendida y perfil local. No es un mapa ni una ubicación real.",
        diagramLabel:
          "Diagrama conceptual: oferta, zona de servicio y perfil local",
      },
      {
        n: "03",
        name: "Google Ads",
        key: "serviceGoogleAds",
        problem:
          "Conecta inversión publicitaria, intención de búsqueda y medición de oportunidades.",
        scope:
          "Alcance: campañas, exclusiones, landing pages y conversiones. Honorarios separados de la inversión publicitaria.",
        cta: "Ver Google Ads",
        caption:
          "Recorrido búsqueda → página de destino → contacto medido. Esquema conceptual, sin cifras.",
        diagramLabel:
          "Diagrama conceptual: búsqueda, página de destino y contacto medido",
      },
    ],
    secondary: [
      { name: "Rediseño web", key: "serviceWebsiteRedesign" },
      { name: "Branding", key: "serviceBranding" },
      { name: "Amazon y e-commerce", key: "serviceAmazon" },
      { name: "E-commerce SEO", key: "serviceEcommerce" },
      { name: "Investigación bilingüe EN/ES", key: "serviceHispanic" },
    ],
  },
  en: {
    aria: "Priority services",
    label: "Priority services",
    title: "Three fronts that turn searches into opportunities.",
    secondaryLabel: "Other fronts, depending on the project",
    services: [
      {
        n: "01",
        name: "Technical SEO",
        key: "serviceTechnical",
        problem:
          "Identifies the barriers that keep search engines from finding and understanding your site.",
        scope:
          "Scope: crawling, indexing, architecture and performance; implementation depends on the engagement.",
        cta: "See technical SEO",
        caption:
          "Page architecture and crawler access. Conceptual diagram, not data from your site.",
        diagramLabel: "Conceptual diagram: page architecture, crawling and indexing",
      },
      {
        n: "02",
        name: "Local SEO",
        key: "localSeoHub",
        problem:
          "Connects your offer with searches from people who need your services in a specific area.",
        scope:
          "Scope: website, business information and eligible local profile. No promised rankings and no invented offices.",
        cta: "See local SEO",
        caption:
          "Service coverage as a concept: offer, served area and local profile. Not a map or a real location.",
        diagramLabel: "Conceptual diagram: offer, service area and local profile",
      },
      {
        n: "03",
        name: "Google Ads",
        key: "serviceGoogleAds",
        problem: "Connects ad spend, search intent and measurement of opportunities.",
        scope:
          "Scope: campaigns, exclusions, landing pages and conversions. Fees are separate from media spend.",
        cta: "See Google Ads",
        caption:
          "Search → landing page → measured enquiry. Conceptual diagram, no figures.",
        diagramLabel: "Conceptual diagram: search, landing page and measured enquiry",
      },
    ],
    secondary: [
      { name: "Website redesign", key: "serviceWebsiteRedesign" },
      { name: "Branding", key: "serviceBranding" },
      { name: "Amazon and ecommerce", key: "serviceAmazon" },
      { name: "Ecommerce SEO", key: "serviceEcommerce" },
      { name: "Bilingual EN/ES research", key: "serviceHispanic" },
    ],
  },
} as const;

export const SceneServices = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language) as unknown as Record<string, string>;
  const diagrams = [DiagramArchitecture, DiagramCoverage, DiagramFunnel];

  return (
    <section className="v3-section bg-white" aria-label={c.aria} id="servicios">
      <div className="v3-shell">
        <SceneHead index="02" label={c.label} title={c.title} />

        <div className="mt-12 lg:mt-16">
          {c.services.map((s, i) => {
            const Diagram = diagrams[i];
            return (
              <article
                key={s.name}
                className="grid items-start gap-8 border-t border-[color:var(--v3-line)] py-10 lg:grid-cols-12 lg:gap-14 lg:py-14"
              >
                <Reveal
                  className={i === 1 ? "lg:order-2 lg:col-span-7" : "lg:col-span-7"}
                  delay={0.04 * i}
                >
                  <p className="v3-micro">
                    <span style={{ color: "var(--v3-blue)" }}>{s.n}</span>
                  </p>
                  <h3
                    className="v3-display mt-3"
                    style={{ fontSize: "clamp(1.45rem, 2.2vw, 2rem)", fontWeight: 700 }}
                  >
                    {s.name}
                  </h3>
                  <p className="v3-body mt-4">{s.problem}</p>
                  <p className="v3-note mt-3">{s.scope}</p>
                  <Link to={routes[s.key]} className="v3-inline-link mt-6">
                    {s.cta}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Reveal>

                <DiagramFrame
                  className={i === 1 ? "lg:order-1 lg:col-span-5" : "lg:col-span-5"}
                  caption={s.caption}
                >
                  <Diagram label={s.diagramLabel} />
                </DiagramFrame>
              </article>
            );
          })}
        </div>

        <div className="mt-10 border-t border-[color:var(--v3-line)] pt-8">
          <p className="v3-micro">{c.secondaryLabel}</p>
          <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-1">
            {c.secondary.map((s) => (
              <li key={s.key}>
                <Link
                  to={routes[s.key]}
                  className="inline-flex min-h-[44px] items-center text-[0.95rem] font-medium text-[color:var(--v3-ink-soft)] underline underline-offset-4 hover:text-[color:var(--v3-blue)]"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
